import { produce } from "immer"
import type { AppAction } from "./../app.types"
import type { IUndoableState, Reducer, UndoableActionConfig } from "./undoable.types"

export function undoableReducer<T, A extends AppAction<any>>(
  reducer: Reducer<T, A>,
  config: UndoableActionConfig
): (state: IUndoableState<T> | undefined, action: A) => IUndoableState<T> {
  const initialState: IUndoableState<T> = {
    past: [],
    present: reducer(undefined, {} as any),
    future: [],
    lastAction: "",
    lastActionTime: new Date().getSeconds()
  }

  return (currentState: IUndoableState<T> | undefined, action: A): IUndoableState<T> => {
    const state = currentState ?? initialState
    const { present } = state

    switch (action.type) {
      case config.undoAction:
        return undo(state)
      case config.redoAction:
        return redo(state)
      case config.clearAction:
        return clear(state)
      default: {
        const newPresent: T = reducer(present, action)

        const isUndoableAction: boolean = config.undoableActions.includes(action.type)
        const isCompoundAction: boolean = config.compoundUndoableActions.includes(action.type)
        const isIgnoreAction: boolean = config.ignoreActions.includes(action.type)
        const isRepeatedAction: boolean = action.type === state.lastAction
        const timeBetweenActions: number = Date.now() - state.lastActionTime
        const compoundTimeExceeded: boolean = timeBetweenActions >= 1000
        const shouldPushPast: boolean =
          isUndoableAction && (!isCompoundAction || (isCompoundAction && (!isRepeatedAction || compoundTimeExceeded)))

        if (present === newPresent) return state

        const result = produce(state, (draftState: IUndoableState<T>) => {
          if (shouldPushPast) {
            draftState.past = [...draftState.past, draftState.present]
            draftState.present = newPresent
            draftState.future = []
          } else {
            draftState.present = newPresent
          }
          if (!isIgnoreAction) {
            draftState.lastAction = action.type
            draftState.lastActionTime = Date.now()
          }
        })
        return result
      }
    }
  }
}

function undo<T>(currentState: IUndoableState<T>): IUndoableState<T> {
  return produce(currentState, (draftState: IUndoableState<T>) => {
    const { past, present, future } = draftState

    if (past.length === 0) return

    const previous = past[past.length - 1]
    const newPast = past.slice(0, past.length - 1)

    draftState.past = newPast
    draftState.present = previous
    draftState.future = [present, ...future]
  })
}

function redo<T>(currentState: IUndoableState<T>): IUndoableState<T> {
  return produce(currentState, (draftState: IUndoableState<T>) => {
    const { past, present, future } = draftState

    if (future.length === 0) return

    const next = future[0]
    const newFuture = future.slice(1)

    draftState.past = [...past, present]
    draftState.present = next
    draftState.future = newFuture
  })
}

function clear<T>(currentState: IUndoableState<T>): IUndoableState<T> {
  return produce(currentState, (draftState: IUndoableState<T>) => {
    draftState.past = []
    draftState.future = []
  })
}
